import { API_CONFIG, buildValidatedApiUrl } from '@/config/api';

export interface ApiResponse<T = any> {
  data?: T;
  message?: string;
  error?: string;
  success: boolean;
}

export class HttpClient {
  private isAuthClient: boolean;

  constructor(options: { auth?: boolean } = {}) {
    this.isAuthClient = options.auth ?? false;
  }

  // Helper method to get stored token
  private getStoredToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('auth_token');
  }

  private async request<T>(
    method: string,
    endpoint: string,
    options: {
      data?: any;
      headers?: Record<string, string>;
      params?: Record<string, string>;
      token?: string;
      auth?: boolean; // 🎯 NEW: Explicit auth control
    } = {}
  ): Promise<ApiResponse<T>> {
    let timeoutId: NodeJS.Timeout | undefined;
    
    try {
      // Check if endpoint is already a full URL (starts with http/https)
      const url = endpoint.startsWith('http') 
        ? new URL(endpoint)
        : new URL(buildValidatedApiUrl(endpoint));
      
      // Add query parameters
      if (options.params) {
        Object.entries(options.params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            url.searchParams.append(key, value);
          }
        });
      }

      // 🔧 CRITICAL: Only send minimal headers for public APIs
      // This prevents nginx from treating public requests as authenticated
      const headers: Record<string, string> = {
        'Accept': 'application/json',
      };

      // 🎯 ROBUST AUTH LOGIC: Multiple layers of protection
      let shouldAddAuth = false;
      let authToken: string | null = null;

      // Layer 1: Explicit auth control (most important)
      if (options.auth === false) {
        // Explicitly disabled - NEVER add auth headers
        shouldAddAuth = false;
      } else if (options.token) {
        // Layer 2: Explicit token provided - validate it properly
        const token = options.token;
        if (typeof token === 'string' && token.trim().length > 0 && 
            token !== 'undefined' && token !== 'null' && token !== 'false') {
          shouldAddAuth = true;
          authToken = token;
        }
      } else if (this.isAuthClient) {
        // Layer 3: Auth client with stored token - but respect explicit auth: false
        if (typeof options.auth === 'undefined' || options.auth === true) {
          const storedToken = this.getStoredToken();
          if (typeof storedToken === 'string' && storedToken.trim().length > 0 && 
              storedToken !== 'undefined' && storedToken !== 'null' && storedToken !== 'false') {
            shouldAddAuth = true;
            authToken = storedToken;
          }
        }
      }

      // Only add auth headers if all validations pass
      if (shouldAddAuth && authToken) {
        headers['Content-Type'] = 'application/json';
        headers['Authorization'] = `Bearer ${authToken}`;
        headers['X-Requested-With'] = 'XMLHttpRequest';
        
        // Debug logging for auth header addition
        if (import.meta.env.DEV) {
          console.log('🔐 Adding auth headers to request:', {
            endpoint,
            method,
            authToken: authToken.substring(0, 10) + '...',
            isAuthClient: this.isAuthClient,
            explicitAuth: options.auth
          });
        }
        
        // Add X-User-ID header if available (required by backend services)
        if (typeof window !== 'undefined') {
          const userId = localStorage.getItem('user_id');
          if (userId && userId !== 'undefined' && userId !== 'null') {
            headers['X-User-ID'] = userId;
          }
        }
      } else {
        // Debug logging for no auth headers
        if (import.meta.env.DEV) {
          console.log('🌐 Public request (no auth headers):', {
            endpoint,
            method,
            shouldAddAuth,
            authToken: authToken ? 'present' : 'none',
            isAuthClient: this.isAuthClient,
            explicitAuth: options.auth
          });
        }
      }

      // Add Content-Type for requests with body, even for public APIs
      if (options.data && ['POST', 'PUT', 'PATCH'].includes(method)) {
        headers['Content-Type'] = 'application/json';
      }

      // Add any additional headers from options
      if (options.headers) {
        Object.assign(headers, options.headers);
      }

      // 🚨 REMOVED: Manual Origin header - browsers set this automatically
      // Manually setting Origin can cause unexpected auth/CORS behavior in nginx

      // Add minimal cache-busting without triggering CORS preflight
      if (method === 'GET') {
        // Use URL parameter instead of custom header to avoid CORS preflight
        const timestamp = Date.now().toString();
        if (options.params) {
          options.params['_cb'] = timestamp;
        } else {
          options.params = { '_cb': timestamp };
        }
      }

      // Add timeout using AbortController (better browser support)
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);
      
      const config: RequestInit = {
        method,
        headers,
        mode: 'cors', // Explicitly set CORS mode
        signal: controller.signal,
      };

      // Add body for POST, PUT, PATCH requests
      if (options.data && ['POST', 'PUT', 'PATCH'].includes(method)) {
        config.body = JSON.stringify(options.data);
      }

      // Only log in development mode
      if (import.meta.env.DEV) {
        console.log(`🌐 ${method} ${url.toString()}`);
      }

      const response = await fetch(url.toString(), config);
      clearTimeout(timeoutId); // Clear timeout on successful response
      
      // Handle different response types
      let responseData: any;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      if (!response.ok) {
        // Enhanced error handling for different HTTP status codes
        let errorMessage = responseData?.error || responseData || `HTTP ${response.status}`;
        
        if (response.status === 504) {
          errorMessage = 'Backend service unavailable (504 Gateway Timeout) - service may be starting up or offline';
        } else if (response.status === 502) {
          errorMessage = 'Backend service unavailable (502 Bad Gateway) - service is offline';
        } else if (response.status === 503) {
          errorMessage = 'Backend service temporarily unavailable (503 Service Unavailable)';
        } else if (response.status === 404) {
          errorMessage = 'API endpoint not found (404) - check service routing';
        }
        
        console.error(`🚨 HTTP ${response.status} Error:`, {
          url: url.toString(),
          method,
          status: response.status,
          statusText: response.statusText,
          responseData,
          requestData: options.data
        });
        
        return {
          success: false,
          error: errorMessage,
          data: responseData
        };
      }

      return {
        success: true,
        data: responseData?.data || responseData,
        message: responseData?.message
      };
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId); // Clear timeout on error
      
      // Only log errors in development mode to reduce console spam
      if (import.meta.env.DEV) {
        console.error('🚨 HTTP Client Error:', { endpoint, error });
      }
      
      // Handle different types of network errors with user-friendly messages
      let errorMessage = 'Network error';
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = 'Request timeout - backend services may be offline or slow to respond';
        } else if (error.message.includes('Failed to fetch')) {
          // This could be CORS, network, or server down
          errorMessage = 'Unable to connect to server - check if backend services are running';
        } else if (error.message.includes('NetworkError')) {
          errorMessage = 'Network connection failed';
        } else if (error.message.includes('CORS')) {
          errorMessage = 'CORS error - check server configuration';
        } else {
          errorMessage = `Connection error: ${error.message}`;
        }
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }
  async get<T>(endpoint: string, options?: { 
    params?: Record<string, string>; 
    token?: string;
    headers?: Record<string, string>;
    auth?: boolean; // 🎯 NEW: Explicit auth control
  }): Promise<ApiResponse<T>> {
    const requestOptions = { ...options };
    
    return this.request<T>('GET', endpoint, requestOptions);
  }

  async post<T>(endpoint: string, data?: any, options?: { 
    token?: string;
    headers?: Record<string, string>;
    auth?: boolean; // 🎯 NEW: Explicit auth control
  }): Promise<ApiResponse<T>> {
    return this.request<T>('POST', endpoint, { ...options, data });
  }

  async put<T>(endpoint: string, data?: any, options?: { 
    token?: string;
    headers?: Record<string, string>;
    auth?: boolean; // 🎯 NEW: Explicit auth control
  }): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', endpoint, { ...options, data });
  }

  async patch<T>(endpoint: string, data?: any, options?: { 
    token?: string;
    headers?: Record<string, string>;
    auth?: boolean; // 🎯 NEW: Explicit auth control
  }): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', endpoint, { ...options, data });
  }

  async delete<T>(endpoint: string, options?: { 
    token?: string;
    headers?: Record<string, string>;
    auth?: boolean; // 🎯 NEW: Explicit auth control
  }): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', endpoint, options);
  }

  // Upload file method
  async uploadFile<T>(
    endpoint: string, 
    file: File, 
    options?: { 
      token?: string;
      additionalData?: Record<string, any>;
      auth?: boolean; // 🎯 NEW: Explicit auth control
    }
  ): Promise<ApiResponse<T>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      if (options?.additionalData) {
        Object.entries(options.additionalData).forEach(([key, value]) => {
          formData.append(key, value);
        });
      }

      const headers: Record<string, string> = {};
      
      // 🎯 ROBUST AUTH LOGIC for file uploads
      let shouldAddAuth = false;
      let authToken: string | null = null;

      if (options?.auth === false) {
        // Explicitly disabled - NEVER add auth headers
        shouldAddAuth = false;
      } else if (options?.token) {
        // Explicit token provided - validate it properly
        const token = options.token;
        if (typeof token === 'string' && token.trim().length > 0 && 
            token !== 'undefined' && token !== 'null' && token !== 'false') {
          shouldAddAuth = true;
          authToken = token;
        }
      } else if (this.isAuthClient) {
        // Auth client with stored token - but respect explicit auth: false
        if (typeof options?.auth === 'undefined' || options?.auth === true) {
          const storedToken = this.getStoredToken();
          if (typeof storedToken === 'string' && storedToken.trim().length > 0 && 
              storedToken !== 'undefined' && storedToken !== 'null' && storedToken !== 'false') {
            shouldAddAuth = true;
            authToken = storedToken;
          }
        }
      }

      // Only add auth headers if all validations pass
      if (shouldAddAuth && authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
        
        // Add X-User-ID header if available
        if (typeof window !== 'undefined') {
          const userId = localStorage.getItem('user_id');
          if (userId && userId !== 'undefined' && userId !== 'null') {
            headers['X-User-ID'] = userId;
          }
        }
      }
      
      // 🚨 REMOVED: Manual Origin header - browsers set this automatically

      const response = await fetch(
        endpoint.startsWith('http') ? endpoint : buildValidatedApiUrl(endpoint), 
        {
          method: 'POST',
          headers,
          body: formData,
          mode: 'cors',
          credentials: 'omit',
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: responseData?.error || `HTTP ${response.status}`,
          data: responseData
        };
      }

      return {
        success: true,
        data: responseData?.data || responseData,
        message: responseData?.message
      };
    } catch (error) {
      console.error('🚨 File Upload Error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload error'
      };
    }
  }
}

// 🎯 Separate clients for public vs authenticated requests
// This prevents accidental auth header leakage to public APIs
export const publicHttpClient = new HttpClient({ auth: false });
export const authHttpClient = new HttpClient({ auth: true });

// Legacy export for backward compatibility (defaults to public)
export const httpClient = publicHttpClient;